<script setup lang="ts">
import WithdrawalPopupController from "@/views/withdrawal-popup-group/WithdrawalPopupController";
import { ref, watch } from "vue";
import Animations from "@/helpers/animations";

interface IEmits {
	(e: 'change'): void
}

const emits = defineEmits<IEmits>();

const viewWithdrawalController = WithdrawalPopupController.getInstance();
const {
	activeRequisite,
	textRequisiteError
} = viewWithdrawalController;

const psystemAlert = ref<HTMLElement | null>(null);
const psystemError = ref<HTMLElement | null>(null);

watch(textRequisiteError, (newValue) => {
	if (newValue) {
		setTimeout(() => {
			if (psystemAlert.value) {
				Animations.shakeAnimation(psystemAlert.value);
			}

			if (psystemError.value) {
				Animations.shakeAnimation(psystemError.value);
			}
		}, 0);
	}
});

const handleChangePSystem = () => {
	emits('change');
}

const makeShortAddress = (value: string) => {
	let result = value.slice(-4);
	return '*'.concat(result);
}
</script>

<template>
	<div class="requisite-wrapper" @click="handleChangePSystem">
		<div class="requisite-wrapper__title">
			<span class="title">{{ $t('withdrawal-create.requisite-wrapper.title') }}</span>
		</div>
		<div class="requisite">
			<template v-if="activeRequisite">
				<div class="left-side">
					<img :src="activeRequisite.psystem.imgPath!">
					<p class="name">{{ activeRequisite?.psystem?.name }}
						<span class="address">{{ makeShortAddress(activeRequisite.address) }}</span>
					</p>
				</div>
				<i class="icon svg-arrow-right"></i>
			</template>
			<template v-else>
				<div class="dimmy-requisite">
					<p>{{ $t('withdrawal-create.requisite-wrapper.to') }}</p>
					<div class="after">
						<Transition name="fade" mode="out-in">
							<i v-if="textRequisiteError" ref="psystemAlert" class="icon i-svg-warning-round-red"></i>
						</Transition>
						<i class="icon svg-arrow-right"></i>
					</div>
				</div>
			</template>
		</div>
		<Transition name="fade" mode="out-in">
			<div v-if="textRequisiteError" ref="psystemError" class="psystem-error">
				<span>{{ $t('withdrawal-create.psystem-error') }}</span>
			</div>
		</Transition>
	</div>
</template>

<style scoped lang="scss">
.requisite-wrapper {
	position: relative;
	margin-top: 24px;
	margin-bottom: 34px;

	&__title {
		display: flex;
		margin-bottom: 10px;
		justify-content: space-between;
		margin-right: 16px;
	}

	.title {
		margin: 0px 16px;
		font-style: normal;
		font-weight: 400;
		font-size: 12px;
		line-height: 14.4px;
		text-transform: uppercase;
		color: #818E95;
	}

	.title + .requisite {
		margin-top: 8px;
	}

	.requisite {
		cursor: pointer;
		position: relative;
		height: 48px;
		display: flex;
		background-color: #FFFFFF;
		align-items: center;
		padding: 0px 16px;

		&:after {
			content: '';
			position: absolute;
			background-color: var(--f7-list-border-color);
			display: block;
			z-index: 15;
			top: auto;
			right: auto;
			bottom: 0;
			left: 0;
			height: 1px;
			width: 100%;
			transform-origin: 50% 100%;
			transform: scaleY(calc(1 / var(--f7-device-pixel-ratio)));
		}

		&:before {
			content: '';
			position: absolute;
			background-color: var(--f7-list-border-color);
			display: block;
			z-index: 15;
			top: 0;
			right: auto;
			bottom: auto;
			left: 0;
			height: 1px;
			width: 100%;
			transform-origin: 50% 0%;
			transform: scaleY(calc(1 / var(--f7-device-pixel-ratio)));
		}

		.left-side {
			display: flex;
			align-items: center;
			flex: 1;
			gap: 8px;

			img {
				width: 24px;
				height: 24px;
			}

			.name {
				@include text(16px, 400, var(--brown1), 19.2px);

				.address {
					@include text(16px, 400, #818E95, 19.2px);
				}
			}
		}

		.dimmy-requisite {
			display: flex;
			align-items: center;
			flex: 1;
			justify-content: space-between;

			p {
				@include text(16px, 400, #BFBFBF);
			}

			.after {
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 8px;
			}
		}
	}

	.psystem-error {
		position: absolute;
		display: flex;
		padding: 6px 16px 0px;
		min-height: 14px;
		@include text(12px, 400, #FF3B30, 14px);
		width: fit-content;
	}
}
</style>
